import { memo, useCallback } from 'react';
import { Button, Separator } from '@monksflow/monks-ui';
import { ExternalLinkIcon, ImageIcon } from 'lucide-react';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { MotionBox } from '@/shared/ui/motion';
import type { BenchmarkCardProps, ImageWithUrlProps } from './BenchmarkCard.types';
import { CAROUSEL_DIMENSIONS } from '../benchmarkCarousel.constants';
import { getWidgetStores } from '@/entities/widget';
import { useTranslation } from 'react-i18next';
import { i18n } from '@/shared/i18n';

const IMAGE_PLACEHOLDER =
  'flex size-full items-center justify-center bg-muted-foreground text-muted';

/**
 * ImageWithUrl - Async image loader for benchmark cards
 * Memoized to prevent re-fetching on parent re-renders
 */
const ImageWithUrl = memo(function ImageWithUrl({ assetId }: ImageWithUrlProps) {
  const { downloadUrl, isLoading } = useGetDownloadUrl(assetId);

  if (isLoading || !downloadUrl) {
    return (
      <div className={IMAGE_PLACEHOLDER}>
        <ImageIcon className="size-12" />
      </div>
    );
  }

  return <img src={downloadUrl} alt="" className="size-full object-cover" />;
});

const TYPE_KEYS = {
  cliches: 'contentTest:ideationWidget.benchmarkCard.type.cliches',
  'disruptive-stunt': 'contentTest:ideationWidget.benchmarkCard.type.disruptiveStunt',
} as const;

// Falls back to title-casing the raw token: the agent can emit a type this map does not cover.
const formatType = (type: string): string =>
  type in TYPE_KEYS
    ? i18n.t(TYPE_KEYS[type as keyof typeof TYPE_KEYS])
    : type.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const ExploreCaseButton = ({
  exporting,
  link,
  onClick,
}: {
  exporting: boolean;
  link: string;
  onClick: () => void;
}) => {
  const { t } = useTranslation('contentTest');
  const label = (
    <>
      {t('ideationWidget.benchmarkCard.exploreCase')}
      <ExternalLinkIcon />
    </>
  );

  // Export renders a real anchor so headless Chromium captures a clickable link.
  return exporting ? (
    <Button variant="secondary" size="default" asChild>
      <a href={link} target="_blank" rel="noopener noreferrer">
        {label}
      </a>
    </Button>
  ) : (
    <Button variant="secondary" size="default" onClick={onClick}>
      {label}
    </Button>
  );
};

/**
 * BenchmarkCard - Displays a benchmark reference in the carousel
 *
 * Features:
 * - Memoized to prevent unnecessary re-renders during carousel transitions
 * - Uses memoized child components for images
 * - Accessible with proper semantic structure
 */
export const BenchmarkCard = ({
  reference,
  isSelected = true,
  isNext = false,
  exporting = false,
}: BenchmarkCardProps) => {
  const { t } = useTranslation('contentTest');

  const imageAssetId = reference.imageAssetId;
  const hasImage = !!imageAssetId;

  const { sidePanelLayoutMode } = getWidgetStores().uiStore.useSidePanelLayoutMode();
  const isFullscreen = sidePanelLayoutMode === 'fullscreen';

  // Memoize the explore case handler
  const handleExploreCase = useCallback(() => {
    if (reference.link) {
      window.open(reference.link, '_blank', 'noopener,noreferrer');
    }
  }, [reference.link]);

  // Format type string
  const formattedType = formatType(reference.type);

  // Build metadata string: "Brand, Agency, Year"
  const metadataString = [
    reference.brand,
    reference.agency || t('ideationWidget.benchmarkCard.agencyUnknown'),
    reference.year,
  ]
    .filter(Boolean)
    .join(', ');

  // Export: no scale (static capture).
  const scaleAnimation = {
    scale: exporting || isSelected ? 1 : 460 / 492,
  };

  // Export: fixed width, not `vw`.
  const imageWidth = exporting
    ? '40%'
    : isSelected
      ? `${(CAROUSEL_DIMENSIONS.mainCardImage.width / 1440) * 100}vw`
      : isFullscreen
        ? `${(153 / 1440) * 100}vw`
        : `${(335 / 1440) * 100}vw`;

  return (
    <div
      className={`flex flex-1 rounded-3xl ${isNext ? 'bg-transparent' : 'bg-background-emphasis'}`}
    >
      {/* Left Image Section */}
      <MotionBox
        initial={false}
        animate={scaleAnimation}
        transition={{ duration: 0.3 }}
        className="h-full shrink-0 overflow-hidden rounded-3xl"
        style={{ width: imageWidth }}
      >
        {hasImage && imageAssetId ? (
          <ImageWithUrl assetId={imageAssetId} />
        ) : (
          <div className={IMAGE_PLACEHOLDER}>
            <ImageIcon className="size-12" />
          </div>
        )}
      </MotionBox>

      {/* Right Content Section */}
      <div
        className={`flex flex-1 flex-col justify-center gap-8 overflow-y-auto rounded-r-3xl p-10 ${
          isNext ? 'bg-surface' : 'bg-background-emphasis'
        }`}
      >
        {/* Text Content */}
        <div className="flex flex-col gap-6">
          {/* Title and Description */}
          <div className="flex flex-col gap-3">
            {/* Category Badge - using 'type' field */}
            {formattedType && (
              <p className="text-body-lg font-medium text-muted-foreground">{formattedType}</p>
            )}

            {/* Title and Description Block */}
            <div className="flex flex-col gap-3">
              <p className="text-heading-xl text-muted-foreground">{reference.title}</p>
              <p className="text-body-lg text-background">{reference.description}</p>
            </div>
          </div>

          <Separator className="bg-border-strong" />

          {/* Metadata Grid - 2 columns */}
          <div className="flex gap-10">
            {/* Universal Truth Column */}
            {reference.universalTruth && (
              <div className="flex flex-1 flex-col gap-3">
                <p className="text-body-lg font-medium text-muted-foreground">
                  {t('ideationWidget.benchmarkCard.universalTruth')}
                </p>
                <p className="text-body-lg text-background">{reference.universalTruth}</p>
              </div>
            )}

            {/* Brand, Agency, Year Column */}
            <div className="flex flex-1 flex-col gap-3">
              <p className="text-body-lg font-medium text-muted-foreground">
                {t('ideationWidget.benchmarkCard.brandAgencyAndYear')}
              </p>
              <p className="text-body-lg text-background">{metadataString}</p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        {reference.link && (
          <div className="flex flex-col items-start">
            <ExploreCaseButton
              exporting={exporting}
              link={reference.link}
              onClick={handleExploreCase}
            />
          </div>
        )}
      </div>
    </div>
  );
};
